
import { useState } from 'react';

export default function LegalContactForm() {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.subject || !formData.message) return;

    setStatus('sending');
    try {
      const body = new URLSearchParams();
      body.append('name', formData.name);
      body.append('email', formData.email);
      body.append('subject', formData.subject);
      body.append('message', formData.message);

      const res = await fetch('https://readdy.ai/api/form/d6e3mvitehdqnvnpj7h0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
        setTimeout(() => setStatus('idle'), 4000);
      } else {
        setStatus('error');
        setTimeout(() => setStatus('idle'), 3000);
      }
    } catch {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  return (
    <section className="max-w-4xl mx-auto mt-16 pt-12 border-t border-neutral-200">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 flex items-center justify-center bg-neutral-900 text-white rounded-lg">
          <i className="ri-mail-line text-lg"></i>
        </div>
        <div>
          <h3 className="text-lg font-bold text-neutral-900" style={{ fontFamily: 'Bebas Neue, sans-serif', letterSpacing: '1px' }}>
            ¿Tienes alguna consulta legal?
          </h3>
          <p className="text-xs text-neutral-500">Escríbenos y te responderemos lo antes posible</p>
        </div>
      </div>

      <form
        id="privacy-contact-form"
        data-readdy-form
        onSubmit={handleSubmit}
        className="bg-neutral-50 border border-neutral-200 rounded-lg p-6 space-y-4"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-neutral-700 mb-1">Nombre *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              className="w-full bg-white border border-neutral-300 rounded-md px-4 py-2.5 text-sm text-neutral-900 focus:outline-none focus:border-neutral-900 transition-colors"
              placeholder="Tu nombre completo"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-700 mb-1">Email *</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full bg-white border border-neutral-300 rounded-md px-4 py-2.5 text-sm text-neutral-900 focus:outline-none focus:border-neutral-900 transition-colors"
              placeholder="tu@email.com"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-neutral-700 mb-1">Asunto *</label>
          <select
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            required
            className="w-full bg-white border border-neutral-300 rounded-md px-4 py-2.5 text-sm text-neutral-900 focus:outline-none focus:border-neutral-900 transition-colors cursor-pointer"
          >
            <option value="">Selecciona un asunto</option>
            <option value="Ejercicio de derechos RGPD">Ejercicio de derechos RGPD</option>
            <option value="Solicitud de eliminación de datos">Solicitud de eliminación de datos</option>
            <option value="Consulta sobre política de privacidad">Consulta sobre política de privacidad</option>
            <option value="Consulta sobre aviso legal">Consulta sobre aviso legal</option>
            <option value="Baja de newsletter">Baja de newsletter</option>
            <option value="Otra consulta legal">Otra consulta legal</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-neutral-700 mb-1">Mensaje *</label>
          <textarea
            name="message"
            value={formData.message}
            onChange={handleChange}
            required
            maxLength={500}
            rows={4}
            className="w-full bg-white border border-neutral-300 rounded-md px-4 py-2.5 text-sm text-neutral-900 focus:outline-none focus:border-neutral-900 transition-colors resize-none"
            placeholder="Describe tu consulta..."
          ></textarea>
          <p className="text-xs text-neutral-400 mt-1 text-right">{formData.message.length}/500</p>
        </div>

        <button
          type="submit"
          disabled={status === 'sending'}
          className="px-8 py-3 bg-neutral-900 text-white text-sm font-medium hover:bg-neutral-800 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed rounded-md"
        >
          {status === 'sending' ? (
            <span className="flex items-center gap-2">
              <i className="ri-loader-4-line animate-spin"></i> Enviando...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <i className="ri-send-plane-line"></i> Enviar Consulta
            </span>
          )}
        </button>

        {status === 'success' && (
          <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-md px-4 py-3 text-sm">
            <i className="ri-check-double-line"></i>
            <span>Tu consulta ha sido enviada correctamente. Te responderemos pronto.</span>
          </div>
        )}
        {status === 'error' && (
          <div className="flex items-center gap-2 text-red-700 bg-red-50 border border-red-200 rounded-md px-4 py-3 text-sm">
            <i className="ri-error-warning-line"></i>
            <span>Ha ocurrido un error. Por favor, inténtalo de nuevo.</span>
          </div>
        )}
      </form>
    </section>
  );
}
